
import { trigger, transition, animate, style, state, query, group } from '@angular/animations';


/**
 * This animation handles fading in and out the list header title
 * on a collapsed list control when the search filter is toggled. The extra complication
 * is required because it should not fade in on ':enter' or else the animation
 * would trigger on every page load. It should only trigger the fade-in animation when the search filter
 * is toggled off (hideSearchFilter).
 * 
 * NOTE: This animation duration should match show/clear search filter delay in retrieval-list
 * and data-entry-list components. This is so the next the component does not enter the view
 * before the previous one has left the view.
 */
export const listHeaderFadeAnimation =
    trigger('fadeInOutTrigger', [
        // End transition with this style
        state('hideSearchFilter', style({ opacity: 1 })),
        // Entering the view with this state
        transition('void => hideSearchFilter', [
            // Start the element with this style 
            style({ opacity: 0 }),
            // Animate from start style to end style
            animate('250ms linear')
        ]),
        // Leaving the view
        transition(':leave', [
            animate('250ms',
                // Animate from any style to transparent
                style({ opacity: 0 }))
        ])
    ]);

/**
 * This animation handles sliding the search filter element up into place
 * on a collapsed list control when the search filter is toggled on. When the search
 * filter is toggled off, the search filter fades out. 
 * 
 * NOTE: This animation duration should match show/clear search filter delay in retrieval-list
 * and data-entry-list components. This is so the next the component does not enter the view
 * before the previous one has left the view.
 */
export const searchFilterSlideAnimation =
    trigger('slideUpTrigger', [
        // Entering the view with this state
        transition(':enter', [
            // Start with bottom border matching focus border of text field.
            // This allows the text field to slide up the appearing like only
            // the placeholder is sliding up. In reality, the entire text field
            // is sliding up with its borders hidden. And this border will appear
            // as the focus border during the transition.
            style({ 'border-bottom': '2px solid #2E3D98' }),
            query('.ot-list-search-form', [
                // Search form is transparent and positioned vertically down 
                style({ opacity: 0, 'margin-top': '0.75rem' })
            ]),
            // group(); Run animations in parellel
            group([
                query('.ot-list-search-form', [
                    // Animate to opaque
                    animate('250ms ease-out', style({ opacity: 1, 'margin-top': '0rem' }))
                ]),
                query('.ot-form-text input', [
                    // Hide borders of the text input field during transition
                    style({ 'border': 'none', 'background': 'transparent' })
                ])
            ])
        ]),
        // Leaving the view
        transition(':leave', [
            // Animate to transparent          
            animate('250ms linear', style({ opacity: 0 }))
        ])
    ]);

/**
 * This animation slides the action toolbar up and down. Action toolbar is usually used
 * above a grid component and part of a condensed header along with a header component.
 * 
 * A header container can be expanded or collapsed and this should be handled in the stylesheet for the component.
 * The height of the header container and the height container transition should also be
 * handled in the component's stylesheet.
 * 
 * There are two parts to this animation. When the action bar enters the DOM, it slides up into
 * place, replacing the header component. It should leave the DOM when no more records in the
 * grid are selected.
 * 
 * There are two states the header container may be in when the action toolbar leaves the DOM, both of those states
 * are handled below. (1) collapsedHeader -> The action toolbar is being displayed on its own (replacing the header).
 * The action bar can just slide down. (2) expandedHeader -- The action toolbar and the header are both being displayed. 
 * In the latter case, the action bar needs to be positioned at the middle of the expanded 
 * header container (6rem / 2 = 3rem) as it collapses out of view as the header container shrinks.
 * 
 */
export const actionBarSlideAnimation =
    trigger('actionBarTrigger', [
        transition(':enter', [
            // Entering the view
            style({ 'transform': 'translateY(3rem)' }),
            // End animation with no tranform
            animate('300ms ease-out', style({ 'transform': 'none' }))
        ]),
        transition('collapsedHeader => void', [
            // Leaving the view with this state
            animate('300ms ease-out', style({ 'transform': 'translateY(3rem)' }))
        ]),
        transition('expandedHeader => void', [
            style({ 'top': '3rem' }), // <-- Keep top at 3rem so collapsing header will disappear the action bar as it collapses
            animate('300ms ease-out', style({ 'transform': 'none' })) // <-- This is meaningless. It's here just to delay removing the action bar from the DOM until the transition completes.
        ])
    ]);

/**
 * This animation slides the header up and down and works with the action toolbar above. It will either replace
 * the action toolbar or appear above it, depending on the state of the header (expanded or not).
 */
export const condensedHeaderSlideAnimation =
    trigger('condensedHeaderTrigger', [
        transition('void => slideDownHeader', [
            // Entering the view with this state
            style({ 'transform': 'translateY(-3rem)' }),
            // End animation with no tranform
            animate('300ms ease-out', style({ 'transform': 'none' }))
        ]),
        transition(':leave', [
            animate('300ms ease-out', style({ 'transform': 'translateY(-3rem)' }))
        ])
    ]);


/**
 * This animation slides a route page in or out from the right side of the screen.
 */
export const routePageSlideInOutAnimation =
    trigger('routeAnimations', [
        transition('HomePage => FolderListPage', [
            // Entering the view with this state
            style({ position: 'relative' }),
            // Make both components absolute positioning
            query(':enter, :leave', [
                style({
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%'
                })
            ]),
            // place the component entering on top
            // and slide it in from the right
            query(':enter', [
                style({ left: '100%', 'z-index': '1001', 'box-shadow': '0px 0px 5px rgba(0, 0, 0, 0.7)' }),
                animate('400ms ease-out', style({ left: '0%' }))
            ]),
        ]),
        transition('HomePage => WorklistPage', [
            // Entering the view with this state
            style({ position: 'relative' }),
            // Make both components absolute positioning
            query(':enter, :leave', [
                style({
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%'
                })
            ]),
            // place the component entering on top
            // and slide it in from the right
            query(':enter', [
                style({ left: '100%', 'z-index': '1001', 'box-shadow': '0px 0px 5px rgba(0, 0, 0, 0.7)' }),
                animate('400ms ease-out', style({ left: '0%' }))
            ]),
        ]),
        transition('FolderListPage => HomePage', [
            // Entering the view with this state
            style({ position: 'relative' }),
            // Make both components absolute positioning
            query(':enter, :leave', [
                style({
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%'
                })
            ]),
            // place the component entering on top
            // and slide it in from the right
            query(':leave', [
                style({ left: '0%', 'z-index': '1001', 'box-shadow': '0px 0px 5px rgba(0, 0, 0, 0.7)' }),
                animate('400ms ease-out', style({ left: '100%' }))
            ]),
        ]),
        transition('WorklistPage => HomePage', [
            // Entering the view with this state
            style({ position: 'relative' }),
            // Make both components absolute positioning
            query(':enter, :leave', [
                style({
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%'
                })
            ]),
            // place the component entering on top
            // and slide it in from the right
            query(':leave', [
                style({ left: '0%', 'z-index': '1001', 'box-shadow': '0px 0px 5px rgba(0, 0, 0, 0.7)' }),
                animate('400ms ease-out', style({ left: '100%' }))
            ]),
        ])
    ]);


export const contentFadeAnimation =
    trigger('fadeInTrigger', [
        // Entering the view
        transition('void => fadeInState', [
            // Find the element that's entering, make it transparent
            query(':enter', [
                style({
                    opacity: 0
                }),
                animate('300ms ease-out'),
                style({
                    opacity: 1
                }),
            ]),
        ]),
    ]);